import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { IBeaconsByFilterQueryVariables, useIBeaconsByFilterQuery } from "../../graphql"
import { IBeaconsList } from "./IBeaconsList"

export type IBeaconsByFilterProps = {
	keyword?: string
	uuid?: string
	major?: number
	minor?: number
}

export const IBeaconsByFilter: React.FC<IBeaconsByFilterProps> = ({ keyword, uuid, major, minor }) => {
	const [filter, setFilter] = useState({ keyword, uuid, major, minor })

	const [pagination, setPagination] = useState<IBeaconsByFilterQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useIBeaconsByFilterQuery({
		variables: { filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, uuid, major, minor })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, uuid, major, minor])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.ibeaconsByFilter.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.ibeaconsByFilter ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading iBeacons</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.ibeaconsByFilter.ibeacons && data.ibeaconsByFilter.ibeacons.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.ibeaconsByFilter.ibeacons.length || 0} iBeacon
						{(data.ibeaconsByFilter.ibeacons.length || 0) > 1 ? "s" : ""}
					</Text>

					<IBeaconsList ibeacons={data.ibeaconsByFilter.ibeacons} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more iBeacons</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any iBeacons.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
